import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Single source of truth for the calendar-date shape the booking quote
// endpoint accepts (`GET /bookings/quote` validates with `new Date(...)` and
// 400s on anything non-ISO). Callers use this to refuse sending a malformed
// date — a stale/corrupted sessionStorage or URL value — to the server, which
// otherwise floods Sentry with an expected `Invalid date format` rejection.
export function isValidIsoDate(value: string | null | undefined): value is string {
  if (typeof value !== "string") return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  // Round-trip the parts so an ISO-shaped but impossible calendar date
  // (e.g. 2026-02-30, which JS would silently roll over to March 2) is
  // rejected rather than forwarded to the quote endpoint.
  const d = new Date(Date.UTC(year, month - 1, day));
  return (
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day
  );
}

export function isUSLocale(countryCode?: string | null): boolean {
  if (countryCode) return countryCode.toUpperCase() === "US";
  if (typeof navigator !== "undefined") {
    const lang = navigator.language || "";
    return lang.endsWith("-US") || lang === "en-US";
  }
  return false;
}

export function formatDate(
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  countryCode?: string | null,
): string {
  if (!dateStr) return "—";
  const locale = isUSLocale(countryCode) ? "en-US" : "en-GB";
  const defaults: Intl.DateTimeFormatOptions = options || { day: "numeric", month: "short", year: "numeric" };
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = dateOnly ? new Date(dateStr + "T12:00:00") : new Date(dateStr);
  return d.toLocaleDateString(locale, defaults);
}

export function formatDateShort(
  dateStr: string | null | undefined,
  countryCode?: string | null,
): string {
  return formatDate(dateStr, { weekday: "short", month: "short", day: "numeric" }, countryCode);
}

export function formatDateLong(
  dateStr: string | null | undefined,
  countryCode?: string | null,
): string {
  return formatDate(dateStr, { weekday: "short", month: "long", day: "numeric", year: "numeric" }, countryCode);
}

export function formatNumber(num: number): string {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}k`;
  }
  return num.toLocaleString();
}

export function currencySymbolPadding(symbol: string): string {
  const len = symbol.length;
  if (len >= 3) return "pl-11";
  if (len === 2) return "pl-9";
  return "pl-7";
}
